import { useState } from "react";
import { CheckCircle2, EyeOff, History, MapPin } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Card from "../card";
import { C, ESCALA, F, TT, UNIMED, Zonas } from "../dashboard/_shared";
import Section from "../section";
import SectionDivider from "../sectionDivider";
import Tag from "../tag";
import "./style.scss";
import { useChartAnimation } from "../charts/printMode";
import {
  allItemsHidden,
  allItemsVisible,
  computeStatDelta,
  corPorValor as corPorValorPure,
  sortCidadesByKey,
  statusForValue,
  visibleItemsCount,
} from "./dataTransforms";
import { metaDefinida } from "../../utils/meta";

export default function SegSection({
  // original props:
  label,
  color,
  segData: segDataProp,
  gerData,
  origemData,
  meta,
  valorGeral,
  domain,
  valueSuffix,
  showZonas,
  cidadesData,
  cidadeKey,
  gerHistData,
  origemHistData,
  cidadesHistData,
  // new props (previously from closure):
  segHistMode,
  toggleSegHist,
  hiddenLines,
  setHiddenLines,
  toggleLine,
}) {
  const animarGraficos = useChartAnimation();
  // Sem meta cadastrada (0) não há linha de meta: antes desenhava "Meta 0".
  const metaRef = metaDefinida(meta);
  const segDataEnriched = segDataProp ?? [];
  const segData = segDataEnriched;
  const LINES = [
    {
      key: "geral",
      name: `${label} Geral`,
      stroke: color,
      width: 2.5,
      dash: "",
      grupo: "geral",
    },
    {
      key: "eGeral",
      name: "Empresa Geral",
      stroke: "#6D28D9",
      width: 2,
      dash: "",
      grupo: "tipo",
    },
    {
      key: "iGeral",
      name: "Individual Geral",
      stroke: "#DB2777",
      width: 2,
      dash: "",
      grupo: "tipo",
    },
    {
      key: "e5p",
      name: "Empresa >5a",
      stroke: "#A78BFA",
      width: 1.5,
      dash: "4 2",
      grupo: "tempo",
    },
    {
      key: "e1a5",
      name: "Empresa >1a e <5a",
      stroke: "#C026D3",
      width: 1.5,
      dash: "4 2",
      grupo: "tempo",
    },
    {
      key: "em1",
      name: "Empresa <1a",
      stroke: "#F472B6",
      width: 1.5,
      dash: "4 2",
      grupo: "tempo",
    },
    {
      key: "i5p",
      name: "Individual >5a",
      stroke: "#06B6D4",
      width: 1.5,
      dash: "4 2",
      grupo: "tempo",
    },
    {
      key: "i1a5",
      name: "Individual >1a e <5a",
      stroke: "#0891B2",
      width: 1.5,
      dash: "4 2",
      grupo: "tempo",
    },
    {
      key: "im1",
      name: "Individual <1a",
      stroke: "#EAB308",
      width: 1.5,
      dash: "4 2",
      grupo: "tempo",
    },
    {
      key: "tit",
      name: "Titular",
      stroke: "#2563EB",
      width: 2,
      dash: "",
      grupo: "papel",
    },
    {
      key: "dep",
      name: "Dependente",
      stroke: "#EA580C",
      width: 2,
      dash: "",
      grupo: "papel",
    },
    // Gerações — adicionadas como linhas pontilhadas com cores próprias
    {
      key: "genZ",
      name: "Gen Z",
      stroke: "#10B981",
      width: 1.5,
      dash: "2 3",
      grupo: "geracao",
    },
    {
      key: "millennial",
      name: "Gen Y",
      stroke: "#3B82F6",
      width: 1.5,
      dash: "2 3",
      grupo: "geracao",
    },
    {
      key: "genX",
      name: "Gen X",
      stroke: "#8B5CF6",
      width: 1.5,
      dash: "2 3",
      grupo: "geracao",
    },
    {
      key: "boomer",
      name: "Baby Boomers",
      stroke: "#F97316",
      width: 1.5,
      dash: "2 3",
      grupo: "geracao",
    },
  ];

  // Cor dinâmica baseada no valor do indicador
  const corPorValor = (v) =>
    corPorValorPure(label, v, {
      green: C.green,
      g700: C.g700,
      red: C.red,
      warn: "#F59E0B",
    });

  // Botão de toggle Consolidado <-> Série
  const ToggleSerie = ({ chartId }) => {
    const isHist = !!segHistMode[chartId];
    return (
      <button
        onClick={() => toggleSegHist(chartId)}
        className={`compSegSection__toggleBtn${isHist ? " is-hist" : ""}`}
        style={{ "--sec-color": color }}
        title={
          isHist
            ? "Mostrando série histórica — clique para ver consolidado"
            : "Mostrando consolidado — clique para ver série histórica"
        }
      >
        <History size={11} />
        {isHist ? "Série histórica" : "Consolidado"}
      </button>
    );
  };

  // Custom GenTick scoped to this section's data
  const GenTick = ({ x, y, payload }) => {
    const gen = gerData.find((g) => g.g === payload.value);
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="middle"
          fill={C.text2}
          fontSize={10}
          fontWeight={600}
        >
          {payload.value}
        </text>
        <text
          x={0}
          y={0}
          dy={25}
          textAnchor="middle"
          fill={C.text4}
          fontSize={9}
        >
          {gen ? `(${gen.range})` : ""}
        </text>
      </g>
    );
  };

  // Ações de marcar/desmarcar todas — opera em hiddenLines pra todas as keys do LINES
  const marcarTodas = () => {
    setHiddenLines((prev) => {
      const novo = { ...prev };
      LINES.forEach((l) => {
        delete novo[l.key];
      });
      return novo;
    });
  };

  const desmarcarTodas = () => {
    setHiddenLines((prev) => {
      const novo = { ...prev };
      LINES.forEach((l) => {
        novo[l.key] = true;
      });
      return novo;
    });
  };

  const todasMarcadas = allItemsVisible(LINES, hiddenLines, (l) => l.key);
  const todasDesmarcadas = allItemsHidden(LINES, hiddenLines, (l) => l.key);

  const [hiddenCidades, setHiddenCidades] = useState({});
  const toggleCidade = (nome) =>
    setHiddenCidades((prev) => ({ ...prev, [nome]: !prev[nome] }));
  const marcarTodasCidades = () => setHiddenCidades({});
  const desmarcarTodasCidades = () =>
    setHiddenCidades(
      Object.fromEntries((cidadesData ?? []).map((c) => [c.nome, true])),
    );

  return (
    <>
      <SectionDivider
        label={`${label} — Segmentações`}
        color={color}
        sub={`Como o ${label} varia por tipo de beneficiário, geração e origem`}
      />

      {/* Multi-line chart */}
      <Section
        title={`${label} por segmento — clique na legenda para ocultar/exibir`}
        pad={false}
      >
        <div className="compSegSection__chartPad">
          {/* Botões de marcar/desmarcar todas */}
          <div className="compSegSection__legendBar">
            <button
              onClick={marcarTodas}
              disabled={todasMarcadas}
              style={{ "--sec-color": color }}
              className={`compSegSection__markBtn compSegSection__markBtn--mark${todasMarcadas ? " is-disabled" : ""}`}
            >
              <CheckCircle2 size={11} /> Marcar todas
            </button>
            <button
              onClick={desmarcarTodas}
              disabled={todasDesmarcadas}
              className={`compSegSection__markBtn compSegSection__markBtn--unmark${todasDesmarcadas ? " is-disabled" : ""}`}
            >
              <EyeOff size={11} /> Desmarcar todas
            </button>
            <div className="compSegSection__legendDivider" />
            <span className="compSegSection__legendCount">
              {visibleItemsCount(LINES, hiddenLines, (l) => l.key)} de{" "}
              {LINES.length} segmentos visíveis
            </span>
            {valorGeral !== undefined && valorGeral !== null && (
              <>
                <div className="compSegSection__legendDivider" />
                <span
                  className="compSegSection__legendCount"
                  style={{ color, fontWeight: 700 }}
                >
                  {label} Geral: {valorGeral}
                  {valueSuffix || ""}
                </span>
              </>
            )}
          </div>
          <div className="compSegSection__legendItems">
            {LINES.map((l) => (
              <button
                key={l.key}
                onClick={() => toggleLine(l.key)}
                className={`compSegSection__legendBtn${hiddenLines[l.key] ? " is-hidden" : ""}`}
                style={{
                  border: `1px solid ${hiddenLines[l.key] ? "var(--color-border)" : l.stroke}`,
                }}
              >
                <div
                  className="compSegSection__legendLine"
                  style={{
                    background: l.stroke,
                    backgroundImage: l.dash
                      ? `repeating-linear-gradient(to right,${l.stroke} 0,${l.stroke} 4px,transparent 4px,transparent 6px)`
                      : "none",
                    backgroundColor: l.dash ? "transparent" : l.stroke,
                  }}
                />
                <span className="compSegSection__legendLabel">{l.name}</span>
              </button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={segDataEnriched} margin={{ top: 4, right: 24 }}>
              {showZonas && Zonas({ tipo: "nps" })}
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={C.border}
                vertical={false}
              />
              <XAxis dataKey="p" tick={{ fontSize: 11, fill: C.text2 }} />
              <YAxis
                domain={domain}
                tick={{ fontSize: 10, fill: C.text4 }}
                tickFormatter={(v) => v + (valueSuffix || "")}
              />
              <Tooltip content={<TT />} />
              {metaRef !== null && (
                <ReferenceLine
                  y={metaRef}
                  stroke={C.p400}
                  strokeDasharray="5 3"
                  label={{
                    value: `Meta ${metaRef}${valueSuffix || ""}`,
                    position: "right",
                    fontSize: 10,
                    fill: C.p600,
                  }}
                />
              )}
              {LINES.map((l) => (
                <Line
                  isAnimationActive={animarGraficos}
                  key={l.key}
                  type="monotone"
                  dataKey={l.key}
                  name={l.name}
                  hide={!!hiddenLines[l.key]}
                  stroke={l.stroke}
                  strokeWidth={l.width}
                  strokeDasharray={l.dash || undefined}
                  dot={{
                    fill: l.stroke,
                    r: hiddenLines[l.key] ? 0 : 3,
                    strokeWidth: 0,
                  }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Titular vs Dependente + Individual vs Empresa */}
      <div className="compSegSection__twoCol">
        <Section title={`${label} — Titular vs Dependente`}>
          <div className="compSegSection__statRow">
            {[
              { l: "Titular", k: "tit", c: "#2563EB" },
              { l: "Dependente", k: "dep", c: "#EA580C" },
            ].map((s) => {
              const { last, delta } = computeStatDelta(segData, s.k);
              const st = statusForValue(last, ESCALA);

              return (
                <div
                  key={s.l}
                  className="compSegSection__statCard"
                  style={{
                    background: st.bg,
                    border: `1px solid ${st.color}25`,
                  }}
                >
                  <div className="compSegSection__statLabel">{s.l}</div>
                  <div
                    className="compSegSection__statValue"
                    style={{ color: st.color }}
                  >
                    {last}
                    {valueSuffix || ""}
                  </div>
                  <div
                    className="compSegSection__statDelta"
                    style={{
                      color:
                        delta > 0 ? C.green : delta < 0 ? C.red : C.text4,
                    }}
                  >
                    {delta > 0 ? "+" : ""}
                    {delta}pts
                  </div>
                  <Tag label={st.label} color={st.color} bg={st.bg} />
                </div>
              );
            })}
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={segData} margin={{ top: 4, right: 16 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={C.border}
                vertical={false}
              />
              <XAxis dataKey="p" tick={{ fontSize: 10, fill: C.text2 }} />
              <YAxis
                domain={domain}
                tick={{ fontSize: 9, fill: C.text4 }}
                tickFormatter={(v) => v + (valueSuffix || "")}
              />
              <Tooltip content={<TT />} />
              {metaRef !== null && (
                <ReferenceLine y={metaRef} stroke={C.p400} strokeDasharray="4 2" />
              )}
              <Line
                isAnimationActive={animarGraficos}
                type="monotone"
                dataKey="tit"
                name="Titular"
                stroke="#2563EB"
                strokeWidth={2}
                dot={{ fill: "#2563EB", r: 3, strokeWidth: 0 }}
              />
              <Line
                isAnimationActive={animarGraficos}
                type="monotone"
                dataKey="dep"
                name="Dependente"
                stroke="#EA580C"
                strokeWidth={2}
                dot={{ fill: "#EA580C", r: 3, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Section>
        <Section title={`${label} — Individual Geral vs Empresa Geral`}>
          <div className="compSegSection__statRow">
            {[
              { l: "Individual Geral", k: "iGeral", c: "#DB2777" },
              { l: "Empresa Geral", k: "eGeral", c: "#6D28D9" },
            ].map((s) => {
              const { last, delta } = computeStatDelta(segData, s.k);
              const st = statusForValue(last, ESCALA);
              return (
                <div
                  key={s.l}
                  className="compSegSection__statCard"
                  style={{
                    background: st.bg,
                    border: `1px solid ${st.color}25`,
                  }}
                >
                  <div className="compSegSection__statLabel">{s.l}</div>
                  <div
                    className="compSegSection__statValue"
                    style={{ color: st.color }}
                  >
                    {last}
                    {valueSuffix || ""}
                  </div>
                  <div
                    className="compSegSection__statDelta"
                    style={{
                      color:
                        delta > 0 ? C.green : delta < 0 ? C.red : C.text4,
                    }}
                  >
                    {delta > 0 ? "+" : ""}
                    {delta}pts
                  </div>
                  <Tag label={st.label} color={st.color} bg={st.bg} />
                </div>
              );
            })}
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={segData} margin={{ top: 4, right: 16 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={C.border}
                vertical={false}
              />
              <XAxis dataKey="p" tick={{ fontSize: 10, fill: C.text2 }} />
              <YAxis
                domain={domain}
                tick={{ fontSize: 9, fill: C.text4 }}
                tickFormatter={(v) => v + (valueSuffix || "")}
              />
              <Tooltip content={<TT />} />
              {metaRef !== null && (
                <ReferenceLine y={metaRef} stroke={C.p400} strokeDasharray="4 2" />
              )}
              <Line
                isAnimationActive={animarGraficos}
                type="monotone"
                dataKey="iGeral"
                name="Individual Geral"
                stroke="#DB2777"
                strokeWidth={2}
                dot={{ fill: "#DB2777", r: 3, strokeWidth: 0 }}
              />
              <Line
                isAnimationActive={animarGraficos}
                type="monotone"
                dataKey="eGeral"
                name="Empresa Geral"
                stroke="#6D28D9"
                strokeWidth={2}
                dot={{ fill: "#6D28D9", r: 3, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Section>
      </div>

      {/* Geração + Local vs Intercâmbio */}
      <div className="compSegSection__twoCol">
        <Section
          title={`${label} por geração`}
          pad={false}
          action={<ToggleSerie chartId={`ger_${label}`} />}
        >
          <div className="compSegSection__sectionPadTop">
            <div className="compSegSection__genRow">
              {gerData.map((g) => {
                const st =
                  ESCALA.find((e) => g.n >= e.min) || ESCALA[ESCALA.length - 1];
                return (
                  <div
                    key={g.g}
                    className="compSegSection__genCard"
                    style={{
                      background: st.bg,
                      border: `1px solid ${st.color}25`,
                    }}
                  >
                    <div className="compSegSection__genLabel">{g.g}</div>
                    <div
                      className="compSegSection__genValue"
                      style={{ color: st.color }}
                    >
                      {g.n}
                      {valueSuffix || ""}
                    </div>
                    <div className="compSegSection__genRange">{g.range}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="compSegSection__sectionPadBottom">
            {!segHistMode[`ger_${label}`] ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={gerData}
                  margin={{ top: 8, right: 16, bottom: 14 }}
                >
                  {showZonas && Zonas({ tipo: "nps" })}
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={C.border}
                  />
                  <XAxis dataKey="g" tick={GenTick} interval={0} height={42} />
                  <YAxis
                    domain={domain}
                    tick={{ fontSize: 10, fill: C.text4 }}
                    tickFormatter={(v) => v + (valueSuffix || "")}
                  />
                  <Tooltip
                    content={({ active, payload, labelArg }) => {
                      if (!active || !(payload && payload.length)) return null;
                      const lbl =
                        payload[0] &&
                        payload[0].payload &&
                        payload[0].payload.g;
                      const g = gerData.find((x) => x.g === lbl);
                      return (
                        <div className="compSegSection__tooltipWrap">
                          <div
                            className="compSegSection__tooltipTitle"
                            style={{ color: C.g300 }}
                          >
                            {lbl}{" "}
                            <span className="compSegSection__tooltipSub">
                              ({g && g.range})
                            </span>
                          </div>
                          <div style={{ color: "#FFFFFF" }}>
                            {label}:{" "}
                            <b>
                              {g && g.n}
                              {valueSuffix || ""}
                            </b>
                          </div>
                        </div>
                      );
                    }}
                  />
                  {metaRef !== null && (
                    <ReferenceLine
                      y={metaRef}
                      stroke={C.p400}
                      strokeDasharray="5 3"
                    />
                  )}
                  <Bar
                    isAnimationActive={animarGraficos}
                    dataKey="n"
                    name={label}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={56}
                  >
                    {gerData.map((g, i) => (
                      <Cell
                        key={i}
                        fill={
                          (
                            ESCALA.find((e) => g.n >= e.min) ||
                            ESCALA[ESCALA.length - 1]
                          ).color
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              (() => {
                const histGer = gerHistData ?? [];
                const cores = [
                  "#7C3AED",
                  "#0284c7",
                  "#16a34a",
                  "#d97706",
                  "#dc2626",
                  "#0891b2",
                ];
                return (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={histGer}
                      margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={C.border}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="p"
                        tick={{ fontSize: 10, fill: C.text2 }}
                      />
                      <YAxis
                        domain={domain}
                        tick={{ fontSize: 10, fill: C.text4 }}
                        tickFormatter={(v) => v + (valueSuffix || "")}
                      />
                      <Tooltip content={<TT />} />
                      <Legend wrapperStyle={{ fontSize: 10, paddingTop: 6 }} />
                      {metaRef !== null && (
                        <ReferenceLine
                          y={metaRef}
                          stroke={C.p400}
                          strokeDasharray="5 3"
                        />
                      )}
                      {gerData.map((g, i) => (
                        <Line
                          isAnimationActive={animarGraficos}
                          key={g.g}
                          type="monotone"
                          dataKey={g.g}
                          name={g.g}
                          stroke={cores[i % cores.length]}
                          strokeWidth={2}
                          dot={{
                            fill: cores[i % cores.length],
                            r: 3,
                            strokeWidth: 0,
                          }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                );
              })()
            )}
          </div>
        </Section>
        <Section
          title={`${label} — Local vs Intercâmbio`}
          pad={false}
          action={<ToggleSerie chartId={`ori_${label}`} />}
        >
          <div className="compSegSection__sectionPadTop">
            <div className="compSegSection__oriRow">
              {origemData.map((o) => {
                const st =
                  ESCALA.find((e) => o.n >= e.min) || ESCALA[ESCALA.length - 1];

                return (
                  <div
                    key={o.t}
                    className="compSegSection__oriCard"
                    style={{
                      background: st.bg,
                      border: `1px solid ${st.color}25`,
                    }}
                  >
                    <div className="compSegSection__oriLabel">{o.t}</div>
                    <div
                      className="compSegSection__oriValue"
                      style={{ color: st.color }}
                    >
                      {o.n}
                      {valueSuffix || ""}
                    </div>
                    <div className="compSegSection__oriResp">
                      {o.resp.toLocaleString()} respondentes
                    </div>
                    <Tag label={st.label} color={st.color} bg={st.bg} />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="compSegSection__sectionPadBottom">
            {!segHistMode[`ori_${label}`] ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={origemData}
                  margin={{ top: 8, right: 16, bottom: 14 }}
                >
                  {showZonas && Zonas({ tipo: "nps" })}
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke={C.border}
                  />
                  <XAxis
                    dataKey="t"
                    tick={{ fontSize: 11, fill: C.text2, fontWeight: 600 }}
                    interval={0}
                    height={42}
                  />
                  <YAxis
                    domain={domain}
                    tick={{ fontSize: 10, fill: C.text4 }}
                    tickFormatter={(v) => v + (valueSuffix || "")}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !(payload && payload.length)) return null;
                      const lbl =
                        payload[0] &&
                        payload[0].payload &&
                        payload[0].payload.t;
                      const o = origemData.find((x) => x.t === lbl);
                      return (
                        <div className="compSegSection__tooltipWrap">
                          <div
                            className="compSegSection__tooltipTitle"
                            style={{ color: C.g300 }}
                          >
                            {lbl}
                          </div>
                          <div style={{ color: "#FFFFFF" }}>
                            {label}:{" "}
                            <b>
                              {o && o.n}
                              {valueSuffix || ""}
                            </b>
                          </div>
                        </div>
                      );
                    }}
                  />
                  {metaRef !== null && (
                    <ReferenceLine
                      y={metaRef}
                      stroke={C.p400}
                      strokeDasharray="5 3"
                    />
                  )}
                  <Bar
                    isAnimationActive={animarGraficos}
                    dataKey="n"
                    name={label}
                    radius={[6, 6, 0, 0]}
                    maxBarSize={80}
                  >
                    {origemData.map((o, i) => (
                      <Cell
                        key={i}
                        fill={
                          (
                            ESCALA.find((e) => o.n >= e.min) ||
                            ESCALA[ESCALA.length - 1]
                          ).color
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              (() => {
                const histOri = origemHistData ?? [];
                const cores = ["#0284c7", "#dc2626", "#16a34a"];
                return (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={histOri}
                      margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={C.border}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="p"
                        tick={{ fontSize: 10, fill: C.text2 }}
                      />
                      <YAxis
                        domain={domain}
                        tick={{ fontSize: 10, fill: C.text4 }}
                        tickFormatter={(v) => v + (valueSuffix || "")}
                      />
                      <Tooltip content={<TT />} />
                      <Legend wrapperStyle={{ fontSize: 10, paddingTop: 6 }} />
                      {metaRef !== null && (
                        <ReferenceLine
                          y={metaRef}
                          stroke={C.p400}
                          strokeDasharray="5 3"
                        />
                      )}
                      {origemData.map((o, i) => (
                        <Line
                          isAnimationActive={animarGraficos}
                          key={o.t}
                          type="monotone"
                          dataKey={o.t}
                          name={o.t}
                          stroke={cores[i % cores.length]}
                          strokeWidth={2}
                          dot={{
                            fill: cores[i % cores.length],
                            r: 3,
                            strokeWidth: 0,
                          }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                );
              })()
            )}
          </div>
        </Section>
      </div>

      {/* === POR CIDADE — agora dentro de cada SegSection === */}
      {cidadesData &&
        cidadesData.length > 0 &&
        (() => {
          const histCid = cidadesHistData ?? [];
          const corCid = (v) => corPorValor(v);
          const sortedCidades = sortCidadesByKey(cidadesData, cidadeKey);
          const CID_CORES = [
            "#16a34a", "#0284c7", "#7C3AED", "#d97706",
            "#dc2626", "#0891b2", "#DB2777", "#92400E",
            "#0f766e", "#b45309", "#4338ca", "#be185d",
          ];
          const todasCidMarcadas = allItemsVisible(sortedCidades, hiddenCidades, (c) => c.nome);
          const todasCidDesmarcadas = allItemsHidden(sortedCidades, hiddenCidades, (c) => c.nome);
          return (
            <Section
              title={`${label} por cidade — área de cobertura ${UNIMED}`}
              pad={false}
              action={<ToggleSerie chartId={`cid_${label}`} />}
            >
              <div className="compSegSection__sectionPad">
                {!segHistMode[`cid_${label}`] ? (
                  <ResponsiveContainer
                    width="100%"
                    height={Math.max(200, sortedCidades.length * 30 + 71)}
                  >
                    <BarChart
                      data={sortedCidades}
                      layout="vertical"
                      barCategoryGap={0.5}
                      margin={{ top: 8, right: 40, left: 8, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        tick={{ fontSize: 10, fill: C.text4 }}
                        tickFormatter={(v) => v + (valueSuffix || "")}
                      />
                      <YAxis
                        type="category"
                        dataKey="nome"
                        tick={{ fontSize: 11, fill: C.text2, fontWeight: 900 }}
                        width={200}
                      />
                      <Tooltip content={<TT />} />
                      <Bar isAnimationActive={animarGraficos} dataKey={cidadeKey} name={label} radius={[0, 6, 6, 0]} maxBarSize={25}>
                        {sortedCidades.map((c, i) => (
                          <Cell key={i} fill={corCid(c[cidadeKey])} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <>
                    <div className="compSegSection__legendBar">
                      <button
                        onClick={marcarTodasCidades}
                        disabled={todasCidMarcadas}
                        style={{ "--sec-color": color }}
                        className={`compSegSection__markBtn compSegSection__markBtn--mark${todasCidMarcadas ? " is-disabled" : ""}`}
                      >
                        <CheckCircle2 size={11} /> Marcar todas
                      </button>
                      <button
                        onClick={desmarcarTodasCidades}
                        disabled={todasCidDesmarcadas}
                        className={`compSegSection__markBtn compSegSection__markBtn--unmark${todasCidDesmarcadas ? " is-disabled" : ""}`}
                      >
                        <EyeOff size={11} /> Desmarcar todas
                      </button>
                      <div className="compSegSection__legendDivider" />
                      <span className="compSegSection__legendCount">
                        {visibleItemsCount(sortedCidades, hiddenCidades, (c) => c.nome)} de {sortedCidades.length} cidades visíveis
                      </span>
                    </div>
                    <div className="compSegSection__legendItems">
                      {sortedCidades.map((c, i) => (
                        <button
                          key={c.nome}
                          onClick={() => toggleCidade(c.nome)}
                          className={`compSegSection__legendBtn${hiddenCidades[c.nome] ? " is-hidden" : ""}`}
                          style={{
                            border: `1px solid ${hiddenCidades[c.nome] ? "var(--color-border)" : CID_CORES[i % CID_CORES.length]}`,
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: hiddenCidades[c.nome] ? "var(--color-border)" : CID_CORES[i % CID_CORES.length],
                              flexShrink: 0,
                            }}
                          />
                          {c.nome}
                        </button>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart data={histCid} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                        <XAxis dataKey="p" tick={{ fontSize: 11, fill: C.text2 }} />
                        <YAxis
                          domain={domain}
                          tick={{ fontSize: 10, fill: C.text4 }}
                          tickFormatter={(v) => v + (valueSuffix || "")}
                        />
                        <Tooltip content={<TT />} />
                        {sortedCidades.map((c, i) => (
                          <Line
                            isAnimationActive={animarGraficos}
                            key={c.nome}
                            type="monotone"
                            dataKey={c.nome}
                            name={c.nome}
                            stroke={CID_CORES[i % CID_CORES.length]}
                            strokeWidth={2}
                            hide={!!hiddenCidades[c.nome]}
                            dot={{ fill: CID_CORES[i % CID_CORES.length], r: 3, strokeWidth: 0 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}

                {/* Cards — sempre visíveis */}
                <div className="compSegSection__cidGrid">
                  {sortedCidades.map((c) => (
                    <Card key={c.nome} style={{ padding: "12px 14px" }}>
                      <div className="compSegSection__cidHeader">
                        <MapPin size={11} color={C.text4} />
                        <div className="compSegSection__cidName">{c.nome}</div>
                        <div className="compSegSection__cidN">n={c.n}</div>
                      </div>
                      <div className="compSegSection__cidValueRow">
                        <div
                          className="compSegSection__cidValue"
                          style={{ color: corCid(c[cidadeKey]), fontFamily: F.display }}
                        >
                          {c[cidadeKey]}{valueSuffix || ""}
                        </div>
                        <div className="compSegSection__cidMetric">{label}</div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </Section>
          );
        })()}
    </>
  );
}
